import React, { useState, useRef, useEffect, useMemo } from "react";
import { Typography } from "@/components/ui/typography";
import ArrowLightMode from "@/assets/svg/arrow_light_mode.svg";
import ArrowDarkMode from "@/assets/svg/arrow_dark_mode.svg";

type DropdownMultiOption = {
  label: string;
  value: string;
};

type DialogMultiSelectDropdownProps = {
  options: DropdownMultiOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  className?: string;
};

const DialogMultiSelectDropdown: React.FC<DialogMultiSelectDropdownProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Select options",
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const allSelected = options.length > 0 && selectedValues.length === options.length;
  const selectionLabel = useMemo(() => {
    if (!selectedValues.length) return placeholder;
    if (selectedValues.length === 1) {
      const selectedOption = options.find((opt) => opt.value === selectedValues[0]);
      return selectedOption?.label ?? placeholder;
    }
    return `${selectedValues.length} years selected`;
  }, [placeholder, selectedValues, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const toggleOption = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((item) => item !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handleSelectAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(options.map((opt) => opt.value));
    }
  };

  return (
    <div ref={dropdownRef} className={`relative min-w-[180px] ${className ?? ""}`}>
      <button
  type="button"
  onClick={() => setIsOpen((prev) => !prev)}
  className="w-full h-[40px] flex items-center justify-between gap-2 px-4 py-2 rounded border border-gray-300 bg-white hover:bg-gray-50 dark:border-gray-600 dark:bg-blue-900 dark:hover:bg-blue-800 text-white transition-colors"
>

        <Typography 
          variant="regular-14" 
          className="truncate"
          style={{ color: "var(--risk-dropdown-text)" }}
        >
          {selectionLabel}
        </Typography>
        <div
          className={`transition-transform flex-shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
          style={{
            width: "24px",
            height: "24px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
          <img
            src={ArrowLightMode}
            alt='arrow'
            className='block dark:hidden w-full h-full'
          />
          <img
            src={ArrowDarkMode}
            alt='arrow'
            className='hidden dark:block w-full h-full'
          />
        </div>
      </button>

      {isOpen && (
        <div
  className="absolute top-full left-0 right-0 mt-1 border border-grey-300 dark:border-gray-600 rounded shadow-lg z-50 max-h-[260px] overflow-y-auto bg-white dark:bg-blue-900"
>

          <DropdownCheckboxRow
            label="Select All"
            checked={allSelected}
            onChange={handleSelectAll}
          />
          <div className="border-t border-gray-200 dark:border-gray-700" />
          {options.map((option, index) => (
            <React.Fragment key={option.value}>
              <DropdownCheckboxRow
                label={option.label}
                checked={selectedValues.includes(option.value)}
                onChange={() => toggleOption(option.value)}
              />
              {index < options.length - 1 && (
                <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

const DropdownCheckboxRow = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) => {
  return (
      <button
        type="button"
        onClick={onChange}
        className="w-full px-4 py-2 flex items-center gap-3 bg-white hover:bg-gray-100 dark:bg-blue-900 dark:hover:bg-blue-800 transition-colors"
        style={{
          // backgroundColor: "transparent",

        }}
      >
        <span
          className={`w-4 h-4 rounded border flex items-center justify-center text-white text-[10px] ${
            checked
              ? "bg-primary-500 border-primary-500"
              : "bg-white border-gray-300 dark:bg-transparent dark:border-gray-500"
          }`}
        >
          {checked && <i className="ri-check-line text-[10px]" />}
        </span>
        <Typography
          variant="medium-14"
          className="whitespace-nowrap"
          style={{ color: "var(--risk-dropdown-text)" }}
        >
          {label}
        </Typography>
      </button>
  );
};

export type { DropdownMultiOption };
export { DialogMultiSelectDropdown };

